import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { getApiErrorMessage } from '../../../../../services/marketplace/errors';
import { useDeleteLeadByExternalIdPhotosPhotoId } from '../../../../../services/marketplace/generated/kubb/hooks/useDeleteLeadByExternalIdPhotosPhotoId';
import { getLeadByExternalIdQueryKey } from '../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import {
  getLeadByExternalIdPhotosQueryKey,
  useGetLeadByExternalIdPhotos,
} from '../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPhotos';
import { usePostLeadByExternalIdPhotos } from '../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdPhotos';
import { PHOTOS_LIVE_VIEW_THRESHOLD } from '../constants';
import { PHOTO_SLOTS } from '../photos/constants';

type PhotoActionState = 'clearing' | 'seeding' | 'seedingPartial' | null;

type UseMarketplacePhotoDevToolsParams = {
  externalId: string;
};

const hashStringToHue = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 360;
  }
  return Math.abs(hash);
};

const createCanvasBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Could not create seed photo blob'));
      },
      'image/jpeg',
      0.92,
    );
  });

const createSeedPhotoFile = async (slotId: string, slotLabel: string): Promise<File> => {
  const canvas = document.createElement('canvas');
  canvas.width = 1400;
  canvas.height = 1050;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not create seed photo canvas');
  }

  const hue = hashStringToHue(slotId);
  const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, `hsl(${hue}, 68%, 45%)`);
  gradient.addColorStop(1, `hsl(${(hue + 40) % 360}, 74%, 34%)`);

  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = 'white';
  context.font = '700 84px Arial';
  context.fillText(slotLabel, 72, 320);

  const blob = await createCanvasBlob(canvas);
  return new File([blob], `${slotId}.jpg`, { type: 'image/jpeg' });
};

const useMarketplacePhotoDevTools = ({ externalId }: UseMarketplacePhotoDevToolsParams) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { refetch: refetchPhotos } = useGetLeadByExternalIdPhotos({ externalId });
  const { mutateAsync: uploadPhoto } = usePostLeadByExternalIdPhotos();
  const { mutateAsync: deletePhoto } = useDeleteLeadByExternalIdPhotosPhotoId();

  const [photoActionState, setPhotoActionState] = useState<PhotoActionState>(null);

  const invalidateLeadAndPhotoQueries = useCallback(async () => {
    if (!externalId) {
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getLeadByExternalIdQueryKey({ externalId }),
      }),
      queryClient.invalidateQueries({
        queryKey: getLeadByExternalIdPhotosQueryKey({ externalId }),
      }),
    ]);
  }, [externalId, queryClient]);

  const clearPersistedPhotos = useCallback(async () => {
    const latestPhotos = (await refetchPhotos()).data ?? [];

    if (latestPhotos.length > 0) {
      await Promise.all(
        latestPhotos.map((photo) =>
          deletePhoto({
            externalId,
            photoId: photo.id,
          }),
        ),
      );
    }

    return latestPhotos.length;
  }, [deletePhoto, externalId, refetchPhotos]);

  const handleDeleteAllPhotos = useCallback(async () => {
    if (!externalId) {
      return;
    }

    setPhotoActionState('clearing');

    try {
      const deletedCount = await clearPersistedPhotos();
      await invalidateLeadAndPhotoQueries();

      toast({
        title: deletedCount > 0 ? 'Deleted all photos' : 'No photos to delete',
        description:
          deletedCount > 0
            ? `${deletedCount} photo${deletedCount === 1 ? '' : 's'} removed from marketplace.`
            : 'Marketplace already has an empty photo set.',
        status: 'success',
        duration: 3500,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'Could not delete photos',
        description: getApiErrorMessage(error, 'Please try again.'),
        status: 'error',
        duration: 4000,
        position: 'top',
      });
    } finally {
      setPhotoActionState(null);
    }
  }, [clearPersistedPhotos, externalId, invalidateLeadAndPhotoQueries, toast]);

  const seedPhotoSet = useCallback(
    async (
      slotsToSeed: typeof PHOTO_SLOTS,
      actionState: PhotoActionState,
      successTitle: string,
    ) => {
      if (!externalId) {
        return;
      }

      setPhotoActionState(actionState);

      try {
        await clearPersistedPhotos();

        await Promise.all(
          slotsToSeed.map(async (slot) => {
            const seedFile = await createSeedPhotoFile(slot.id, slot.label);
            return uploadPhoto({
              externalId,
              data: {
                file: seedFile,
                label: slot.id,
                photoType: slot.photoType,
              },
            });
          }),
        );

        await invalidateLeadAndPhotoQueries();

        toast({
          title: successTitle,
          description: `${slotsToSeed.length} photos uploaded to marketplace.`,
          status: 'success',
          duration: 3500,
          position: 'top',
        });
      } catch (error) {
        toast({
          title: 'Could not seed photos',
          description: getApiErrorMessage(error, 'Please try again.'),
          status: 'error',
          duration: 4000,
          position: 'top',
        });
      } finally {
        setPhotoActionState(null);
      }
    },
    [clearPersistedPhotos, externalId, invalidateLeadAndPhotoQueries, toast, uploadPhoto],
  );

  const handleSeedPrefilledPhotoSet = useCallback(
    () => seedPhotoSet(PHOTO_SLOTS, 'seeding', 'Seeded prefilled photo set'),
    [seedPhotoSet],
  );

  const handleSeedThresholdPhotoSet = useCallback(
    () =>
      seedPhotoSet(
        PHOTO_SLOTS.slice(0, PHOTOS_LIVE_VIEW_THRESHOLD),
        'seedingPartial',
        'Seeded threshold photo set',
      ),
    [seedPhotoSet],
  );

  return {
    photoActionState,
    handleDeleteAllPhotos,
    handleSeedPrefilledPhotoSet,
    handleSeedThresholdPhotoSet,
  };
};

export default useMarketplacePhotoDevTools;
